import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Search, Trash2 } from "lucide-react";

const STATUSES = ["nuevo", "contactado", "cerrado", "perdido"];
const COLORS = { nuevo: "#00F0FF", contactado: "#A78BFA", cerrado: "#00FF66", perdido: "#FF3366" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (filter) params.status = filter;
    if (q) params.q = q;
    const { data } = await api.get("/admin/leads", { params });
    setLeads(data.leads);
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success("Estado actualizado");
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };
  const removeLead = async (id) => {
    if (!window.confirm("¿Eliminar prospecto?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Eliminado");
    load();
    if (selected?.id === id) setSelected(null);
  };

  const exportCsv = () => {
    const headers = ["name","phone","email","company","vehicle_type","units","plan","status","created_at"];
    const rows = leads.map(l => headers.map(h => `"${(l[h]??"").toString().replace(/"/g,'""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `xega-leads-${Date.now()}.csv`; a.click();
  };

  return (
    <div data-testid="admin-leads">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono mb-1">/ Prospectos</div>
          <h1 className="font-display text-4xl chrome-text">Pipeline de cotizaciones</h1>
        </div>
        <button onClick={exportCsv} className="btn-ghost-chrome" data-testid="export-csv">Exportar CSV</button>
      </header>

      <div className="glass-card chrome-border p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-black/40 border border-white/10 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-white/40" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar nombre, teléfono..." className="bg-transparent flex-1 outline-none text-sm" data-testid="leads-search" />
        </div>
        <select value={filter} onChange={(e)=>setFilter(e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="leads-filter">
          <option value="">Todos los estados</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 glass-card chrome-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="label-mono text-left">
                <tr className="border-b border-white/5">
                  <th className="py-3 px-4">Nombre</th><th className="py-3 px-4">Teléfono</th>
                  <th className="py-3 px-4">Estado</th><th className="py-3 px-4">Fecha</th><th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-white/40">Sin prospectos</td></tr>}
                {leads.map((l) => (
                  <tr key={l.id} className={`border-b border-white/5 cursor-pointer hover:bg-white/5 ${selected?.id===l.id?"bg-cyan-400/5":""}`} onClick={()=>setSelected(l)} data-testid={`lead-row-${l.id}`}>
                    <td className="py-3 px-4 text-white">{l.name}</td>
                    <td className="py-3 px-4 text-white/70">{l.phone}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-full text-xs border" style={{ borderColor: `${COLORS[l.status]}55`, color: COLORS[l.status], background: `${COLORS[l.status]}15` }}>{l.status}</span>
                    </td>
                    <td className="py-3 px-4 label-mono">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4"><button onClick={(e)=>{e.stopPropagation(); removeLead(l.id);}} className="text-white/40 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lg:col-span-2 glass-card chrome-border p-6" data-testid="lead-detail">
          {!selected ? <div className="text-white/40 label-mono text-center py-12">Selecciona un prospecto</div> : (
            <>
              <div className="label-mono mb-2">{new Date(selected.created_at).toLocaleString()}</div>
              <h3 className="font-display text-2xl chrome-text">{selected.name}</h3>
              <div className="mt-4 space-y-2 text-sm">
                <Row k="Teléfono" v={selected.phone} />
                <Row k="Email" v={selected.email || "—"} />
                <Row k="Empresa" v={selected.company || "—"} />
                <Row k="Tipo vehículo" v={selected.vehicle_type || "—"} />
                <Row k="Unidades" v={selected.units || "—"} />
                <Row k="Plan interés" v={selected.plan || "—"} />
                <Row k="Mensaje" v={selected.message || "—"} />
              </div>
              <div className="mt-5">
                <div className="label-mono mb-2">Estado</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map(s => (
                    <button key={s} onClick={()=>changeStatus(selected.id, s)} data-testid={`status-${s}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-tech border transition ${selected.status===s?"bg-cyan-400 text-black border-cyan-400":"border-white/10 text-white/70 hover:border-white/30"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <a href={`https://wa.me/${(selected.phone||"").replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="btn-metal w-full mt-6" data-testid="lead-wa">Contactar por WhatsApp</a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-3"><span className="label-mono">{k}</span><span className="text-white text-right">{v}</span></div>;
}
